import styles from "./catalog-view.module.css";
import { useState, useEffect, useMemo, useContext } from "react";
import { Outlet } from "react-router";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import {
  Flex,
  App,
  Menu,
  Spin,
  Splitter,
  ConfigProvider,
  Breadcrumb,
  Space,
  Divider,
  Typography,
  Button,
  Tooltip,
} from "antd";

import HomeIcon from "@/icons/HomeIcon";
import FolderIcon from "@/icons/FolderIcon";
import SearchIcon from "@/icons/Search";
import FilterIcon from "@/icons/FilterIcon";
import CloseIcon from "@/icons/CloseIcon";

import CatalogContext from "./context";
import useCatalogFilters from "./useCatalogFilters";
import { FiltersModalContent } from "./FiltersModalContent";

import { UserContext } from "@/context/UserContext";
import { cleanParams } from "@/utils";

import Breadcrumbs from "@/components/ui/Breadcrumbs";
import CustomSelect from "@/components/inputs/CustomSelect";
import SearchFrom3 from "@/components/inputs/SearchFrom3";
import CustomTreeSelect from "@/components/inputs/CustomTreeSelect";

import FiltersModal from "@/components/modals/FiltersModal";

import { getFolders } from "@/api/files";

import routes from "@/routes";

import { FILES_TYPES } from "@/constants/files";

// Макет для каталога файлов
const CatalogView = ({ isAdmins = false }) => {
  const { unit_uuid, id } = useParams();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();

  const { user } = useContext(UserContext);

  // Данные
  const [unitData, setUnitData] = useState(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [authorSelectOpen, setAuthorSelectOpen] = useState(false);
  const [isCompactCatalog, setIsCompactCatalog] = useState(
    window.innerWidth < 960,
  );

  const {
    filters,
    setFilters,
    fileType,
    setFileType,
    authorUuid,
    setSearch,
    search,
    isAnyFilterApplied,
    handleSelectUnit,
    handleSelectAuthor,
    openFiltersModal,
    clearFilters,
    isFiltersModalShow,
    setIsFiltersModalShow,
    tempValueFilters,
    handleTempFilterChange,
    applyFilters,
    fetchOptions,
    units,
    unitsTree,
    authors,
    getFlatUnits,
    optionsPending,
  } = useCatalogFilters({
    locationSearch: location.search,
    unit_uuid,
    isAdmins,
    navigate,
    setUnitData,
    message,
  });

  const transformDataToMenuItems = (items) => {
    const cleanedFilters = cleanParams(filters);
    const params = new URLSearchParams(cleanedFilters);

    return items.map((item) => ({
      label: (
        <Link
          to={
            unit_uuid
              ? routes.unitFolder(unit_uuid, item.uuid)
              : `${
                  isAdmins
                    ? routes.administration.folder(item.uuid)
                    : routes.folder(item.uuid)
                }?${params.toString()}`
          }
          onClick={(e) => e.stopPropagation()}
        >
          {item.name}
        </Link>
      ),
      key: item.uuid,
      title: item.name,
      icon: <FolderIcon />,
      ...(item.children?.length && {
        children: transformDataToMenuItems(item.children),
      }),
    }));
  };

  const fetchData = async () => {
    setIsLoading(true);

    const cleanedFilters = cleanParams(filters);
    const params = new URLSearchParams(cleanedFilters);

    try {
      //  Получаем структуру папок для бокового меню
      const data = await getFolders(
        {
          ...filters,
          units_uuids: Array.isArray(filters.units_uuids)
            ? filters.units_uuids.map(({ value }) => value)
            : [],
          type_filter: fileType,
        },
        isAdmins,
      );

      const transformedData = transformDataToMenuItems(data);

      setData([
        {
          label: (
            <Link
              to={
                unit_uuid
                  ? routes.unitFolder(unit_uuid)
                  : `${
                      isAdmins
                        ? routes.administration.folder()
                        : routes.folder()
                    }?${params.toString()}`
              }
              onClick={(e) => e.stopPropagation()}
            >
              Папка
            </Link>
          ),
          key: "catalog",
          title: "Папка",
          icon: <HomeIcon />,
          children: transformedData,
        },
      ]);

      if (unit_uuid && id === "catalog" && transformedData?.length) {
        navigate(routes.unitFolder(unit_uuid, transformedData[0].key));
      }

      if (!unit_uuid && filters.units_uuids?.length) {
        navigate(routes.folder());
      }
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line
  }, [unit_uuid]);

  useEffect(() => {
    const handleResize = () => {
      setIsCompactCatalog(window.innerWidth < 960);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Сменили фильтры, сменили URL
  useEffect(() => {
    const f = { ...filters };

    delete f.units_uuids;
    const cleanedFilters = cleanParams(f);

    const currentParams = new URLSearchParams(location.search);

    // сохраняем file_uuid если есть
    const file_uuid = currentParams.get("file_uuid");

    const nextParams = new URLSearchParams(cleanedFilters);

    if (file_uuid) {
      nextParams.set("file_uuid", file_uuid);
    }

    navigate({ search: nextParams.toString() }, { replace: true });

    fetchData();
    // eslint-disable-next-line
  }, [filters]);

  const selectedFolders = useMemo(() => {
    if (id === "catalog") {
      return ["catalog"];
    } else {
      // в data хранится иерархическая структура
      // Нужно найти путь к выбранной папке, открыть все папки на пути, включая текущую
      const findPath = (data, id) => {
        for (const item of data) {
          if (item.key === id) {
            return [id];
          }

          if (item.children) {
            const path = findPath(item.children, id);
            if (path) {
              return [item.key, ...path];
            }
          }
        }
      };

      return findPath(data, id);
    }
    // eslint-disable-next-line
  }, [location, data, unit_uuid]);

  const breadcrumbItems = useMemo(() => {
    const cleanedFilters = cleanParams(filters);
    const params = new URLSearchParams(cleanedFilters);

    if (id === "catalog") {
      return [];
    } else {
      // в data хранится иерархическая структура
      // Нужно найти путь к выбранной папке, и составить из них хлебные крошки, включая саму папку
      const findPath = (data, id) => {
        for (const item of data) {
          if (item.key === id) {
            return [{}];
          }

          if (item.children) {
            const path = findPath(item.children, id);
            if (path) {
              return [
                {
                  title: (
                    <Link
                      to={
                        unit_uuid
                          ? routes.unitFolder(unit_uuid, item.key)
                          : `${
                              isAdmins
                                ? routes.administration.folder(item.key)
                                : routes.folder(item.key)
                            }?${params.toString()}`
                      }
                    >
                      <Space>
                        {item.key === "catalog" && <HomeIcon />}
                        {item.title}
                      </Space>
                    </Link>
                  ),
                },
                ...path,
              ];
            }
          }
        }
      };

      return findPath(data, id);
    }
    // eslint-disable-next-line
  }, [location, data, unit_uuid]);

  const handleSearch = async (val) => {
    setSearch(val);
    setIsLoading(true);
    const cleanedFilters = cleanParams(filters);
    const params = new URLSearchParams(cleanedFilters);

    if (val) {
      setData([
        {
          label: "Результаты поиска",
          key: "search",
          title: "Результаты поиска",
          icon: <SearchIcon />,
          disabled: true,
        },
      ]);

      navigate(
        `${
          isAdmins
            ? routes.administration.folder("search")
            : routes.folder("search")
        }?${params.toString()}`,
        { replace: true },
      );
    } else {
      navigate(
        `${
          isAdmins ? routes.administration.folder() : routes.folder()
        }?${params.toString()}`,
        {
          replace: true,
        },
      );
      fetchData();
    }
    setIsLoading(false);
  };

  const contentBlock = (
    <Flex
      gap={5}
      vertical
      style={{
        height: isCompactCatalog ? "initial" : "100%",
        minHeight: 0,
        flex: 1,
      }}
    >
      {!search && !!breadcrumbItems?.length && (
        <Breadcrumb items={breadcrumbItems} />
      )}
      <CatalogContext.Provider
        value={{
          fetchCatalog: fetchData,
          file_type: fileType,
          units_uuids: filters.units_uuids,
          author_uuid: authorUuid,
          units,
          unit_uuid: filters.unit_uuid,
          unit_in_path: unit_uuid,
          search,
          setSearch: (val) => {
            setSearch(val);
            fetchData();
          },
          isCompactCatalog,
        }}
      >
        <Outlet />
      </CatalogContext.Provider>
    </Flex>
  );

  return (
    <>
      {optionsPending ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        <Flex vertical gap={20} className="h-100">
          <Breadcrumbs
            items={
              isAdmins
                ? [
                    {
                      title: "Административная панель",
                    },
                    {
                      title: "Все файлы",
                    },
                  ]
                : [
                    {
                      title: "Главная",
                      href: routes.dashboards,
                    },
                    unit_uuid ? { title: unitData?.name } : {},
                    {
                      title: "Файлы",
                    },
                  ]
            }
          />

          <Flex gap={8} align="center">
            <Flex gap={8} align="center" className={styles.desktopFilters}>
              {!unit_uuid &&
                (isAdmins ? (
                  <CustomSelect
                    placeholder="Рабочая группа"
                    options={units}
                    fieldNames={{
                      label: "name",
                      value: "uuid",
                      key: "uuid",
                    }}
                    style={{
                      width: 200,
                    }}
                    showSearch
                    optionFilterProp="name"
                    allowClear
                    value={filters.unit_uuid}
                    onChange={(unit) => handleSelectUnit(unit)}
                  />
                ) : (
                  <>
                    <CustomTreeSelect
                      placeholder="Рабочая группа"
                      data={unitsTree}
                      value={filters.units_uuids}
                      onChange={(units_uuids) => {
                        setFilters({ ...filters, units_uuids });
                        setFileType("all");
                      }}
                      style={{
                        width: "200px",
                      }}
                    />
                    <CustomSelect
                      placeholder="Тип"
                      options={FILES_TYPES}
                      style={{
                        width: 135,
                      }}
                      popupMatchSelectWidth={false}
                      value={fileType}
                      onChange={(val) => {
                        setFileType(val);
                        val === "all"
                          ? setFilters({ ...filters, units_uuids: [] })
                          : val === "root"
                            ? setFilters({
                                ...filters,
                                units_uuids: getFlatUnits.filter(
                                  ({ readOnly }) => !readOnly,
                                ),
                              })
                            : setFilters({
                                ...filters,
                                units_uuids: getFlatUnits.filter(
                                  ({ readOnly }) => readOnly,
                                ),
                              });
                      }}
                      optionRender={(option) => (
                        <Flex gap={5} align="center">
                          {option?.data?.icon}
                          <span>{option.label}</span>
                        </Flex>
                      )}
                      labelRender={(option) => {
                        const item = FILES_TYPES.find(
                          (item) => item.value === option.value,
                        );

                        return (
                          <Flex gap={5} align="center">
                            {item?.icon}
                            <span>{item.label}</span>
                          </Flex>
                        );
                      }}
                    />
                  </>
                ))}

              <CustomSelect
                open={authorSelectOpen}
                onOpenChange={setAuthorSelectOpen}
                placeholder="Автор"
                value={authorUuid}
                options={authors}
                showSearch
                allowClear
                fieldNames={{ value: "uuid" }}
                optionFilterProp="label"
                style={{
                  width: 200,
                }}
                onChange={(val) => handleSelectAuthor(val)}
                popupRender={(menu) => (
                  <>
                    {menu}
                    {authors
                      .map((u) => u.uuid)
                      .includes(user.employee_uuid) && (
                      <>
                        <Divider
                          style={{
                            margin: "8px 0",
                          }}
                        />
                        <Space
                          style={{
                            padding: "0 8px 4px",
                          }}
                        >
                          <Typography.Link
                            onClick={() => {
                              handleSelectAuthor(user.employee_uuid);
                              setAuthorSelectOpen(false);
                            }}
                          >
                            Я автор
                          </Typography.Link>
                        </Space>
                      </>
                    )}
                  </>
                )}
              />
            </Flex>

            <SearchFrom3
              placeholder="Поиск"
              style={{ flex: 1, minWidth: 200 }}
              defaultValue={search}
              onSearch={(search) => handleSearch(search)}
            />

            <Flex gap={8} align="center" className={styles.mobileFilters}>
              <Button
                type="primary"
                icon={<FilterIcon />}
                onClick={openFiltersModal}
              ></Button>

              {isAnyFilterApplied && (
                <Tooltip title="Очистить все фильтры" placement="bottom">
                  <Button
                    color="default"
                    variant="link"
                    shape="circle"
                    size="small"
                    icon={<CloseIcon />}
                    onClick={clearFilters}
                    style={{ background: "#BBBBBB", color: "#fff" }}
                  ></Button>
                </Tooltip>
              )}
            </Flex>
          </Flex>

          {isLoading ? (
            <div className="spin">
              <Spin size="large" />
            </div>
          ) : isCompactCatalog ? (
            contentBlock
          ) : (
            <Splitter style={{ flex: 1, minHeight: 0 }}>
              <Splitter.Panel
                key="catalog-menu"
                defaultSize={200}
                min={200}
                max="50%"
                style={{
                  background: "var(--color-bg-layout)",
                  padding: 0,
                }}
              >
                <ConfigProvider
                  theme={{
                    components: {
                      Menu: {
                        subMenuItemBg: "transparent",
                        itemHoverBg: "#f0840017",
                        itemMarginBlock: 0,
                        itemMarginInline: 0,
                        itemBorderRadius: 0,
                        itemColor: "var(--color-gray-d)",
                        itemSelectedBg: "var(--color-white)",
                        itemSelectedColor: "var(--color-text-base)",
                        subMenuItemSelectedColor: "var(--color-text-base)",
                      },
                    },
                  }}
                >
                  <Menu
                    items={data}
                    mode="inline"
                    defaultOpenKeys={selectedFolders}
                    selectedKeys={id}
                    className="custom-catalog"
                    inlineIndent={10}
                  />
                </ConfigProvider>
              </Splitter.Panel>

              <Splitter.Panel
                key="catalog-content"
                style={{
                  paddingLeft: 20,
                }}
              >
                {contentBlock}
              </Splitter.Panel>
            </Splitter>
          )}

          {isFiltersModalShow && (
            <FiltersModal
              visible={isFiltersModalShow}
              onSave={applyFilters}
              onCancel={() => {
                setIsFiltersModalShow(false);
              }}
              onClear={() => {
                setIsFiltersModalShow(false);
                clearFilters();
              }}
            >
              <FiltersModalContent
                isAdmins={isAdmins}
                unit_uuid={unit_uuid}
                tempValueFilters={tempValueFilters}
                units={units}
                unitsTree={unitsTree}
                authors={authors}
                user={user}
                handleTempFilterChange={handleTempFilterChange}
              />
            </FiltersModal>
          )}
        </Flex>
      )}
    </>
  );
};

export default CatalogView;
